import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, OnInit, computed, inject, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Route, Router, RouterModule } from '@angular/router';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  ComboboxComponent,
  ComboboxInputDirective,
  ComboboxSingleItemComponent,
  FormFieldModule,
  HeaderCenterDirective,
  HeaderComponent,
  HeaderEndDirective,
  HeaderMobileMenuActionComponent,
  HeaderMobileMenuComponent,
  HeaderMobileMenuEndDirective,
  HeaderMobileMenuStartDirective,
  HeaderNavigationLinkComponent,
  IconButtonComponent,
  IconComponent,
  SearchComponent,
  SearchInputDirective,
} from '@totalenergiescode/tds-angular';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs';

import { User } from '../../../app/administration/users/user.model';
import { UsersService } from '../../../app/administration/users/users.service';
import { SearchBridgeService } from '../../../app/services/search-bridge.service';
import { environment } from '../../../environments/environment';
import { MpsPermissionHelper } from '../../helpers/mps-permission.helper';
import { mpsRouteNames } from '../../mps-route-names';
import { MpsNotificationService } from '../../services/mps-notification.service';
import { MpsUserService } from '../../services/mps-user.service';

@Component({
  selector: 'mps-app-header',
  templateUrl: './mps-header.component.html',
  styleUrls: ['./mps-header.component.scss'],
  imports: [
    TranslateModule,
    IconComponent,
    HeaderComponent,
    HeaderCenterDirective,
    HeaderEndDirective,
    HeaderMobileMenuComponent,
    HeaderMobileMenuStartDirective,
    HeaderMobileMenuEndDirective,
    HeaderMobileMenuActionComponent,
    HeaderNavigationLinkComponent,
    IconButtonComponent,
    SearchComponent,
    SearchInputDirective,
    FormFieldModule,
    ComboboxComponent,
    ComboboxInputDirective,
    ComboboxSingleItemComponent,
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
  ],
})
export class MpsHeaderComponent implements OnInit {
  mpsUserService = inject(MpsUserService);
  _notificationService = inject(MpsNotificationService);
  translateService = inject(TranslateService);
  private eRef = inject(ElementRef);
  private router = inject(Router);
  user = signal(this.mpsUserService.getUserSubject().getValue());
  avatarLoadFailed = signal(false);
  avatarUrl = computed(() => this.user()?.avatar_url ?? null);
  isDropdownOpen = signal(false);
  isUserMenuOpen = signal(false);
  logoutRoute = mpsRouteNames.auth + '/' + mpsRouteNames.logout;
  readonly canImpersonate = environment.allowImpersonation;
  private usersService = inject(UsersService);
  private searchBridge = inject(SearchBridgeService, { optional: true });
  search = new FormControl();
  searchQuery = new FormControl<string>('');
  usersToImpersonate = signal<User[]>([]);
  isEnvProd = environment.production;
  envName = environment.envName;
  isMobileSubMenuOpen = signal(false);
  readonly navigationRoutes: Route[] = this.router.config;

  ngOnInit(): void {
    this.search.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        filter((searchInput) => searchInput.length >= 3)
      )
      .subscribe((searchInput) => {
        this.usersService.usersToImpersonate(searchInput).subscribe({
          next: (users) => {
            this.usersToImpersonate.set(users);
          },
          error: () => {
            this.usersToImpersonate.set([]);
            this._notificationService.error(this.translateService.instant('messages.api_response.generic_error'));
          },
        });
      });
    this.search.valueChanges.subscribe((v) => this.searchBridge?.push(v ?? ''));
    this.searchQuery.valueChanges.subscribe((v) => this.searchBridge?.push(v ?? ''));
  }

  onSearchInputFocus(): void {
    this.searchBridge?.setVisible(true);
  }

  getCurrentLangLabel(): string {
    return this.getLangLabel(this.translateService.currentLang);
  }

  getLangLabel(lang: string): string {
    const labels: Record<string, string> = {
      fr: 'Français',
      en: 'English',
    };
    return labels[lang] || this.translateService.instant('general.missing_translation');
  }

  toggleDropdown() {
    this.isDropdownOpen.set(!this.isDropdownOpen());
    this.isUserMenuOpen.set(false);
  }

  toggleUserMenu() {
    this.isUserMenuOpen.set(!this.isUserMenuOpen());
    this.isDropdownOpen.set(false);
  }

  switchLanguage(language: string) {
    this.translateService.use(language);
    const user = this.mpsUserService.getUser();
    localStorage.setItem('lang', language);
    if (user) {
      this.mpsUserService.changeLang(user.id, language)?.subscribe();
    }
  }

  onAvatarError(): void {
    this.avatarLoadFailed.set(true);
  }

  getUserDisplayName() {
    return this.user()?.firstname + ' ' + this.user()?.lastname.substring(0, 1) + '.';
  }

  @HostListener('document:click', ['$event'])
  handleClickOutside(event: Event) {
    if (!this.eRef.nativeElement.contains(event.target)) {
      this.isDropdownOpen.set(false);
      this.isUserMenuOpen.set(false);
    }
  }

  impersonateUser(event: { value: User | undefined; label: string }): void {
    if (event.value) {
      this.impersonate(event.value);
    }
  }

  impersonate(user: User): void {
    this.usersService.impersonate(user.id).subscribe({
      next: (token: string) => {
        localStorage.setItem('token', token);
        window.location.reload();
      },
      error: () => {
        this._notificationService.error(this.translateService.instant('messages.api_response.generic_error'));
      },
    });
  }

  isMenuItemDisplayed(route: Route): boolean {
    const user = this.user();
    if (!user) {
      return false;
    }
    const hasTitle = route.data?.['title'];
    const canUserActivate = MpsPermissionHelper.canUserActivate(user, route);
    return hasTitle && canUserActivate;
  }

  isChildRouteActive(route: Route): boolean {
    if (!route?.children) {
      return false;
    }
    return route.children.some((child: Route) =>
      this.router.isActive(this.router.createUrlTree([route.path, child.path]), {
        paths: 'subset',
        queryParams: 'ignored',
        fragment: 'ignored',
        matrixParams: 'ignored',
      })
    );
  }

  toggleMobileSubMenu(): void {
    this.isMobileSubMenuOpen.set(!this.isMobileSubMenuOpen());
  }
}
